import React, { useRef, useCallback, useState } from 'react';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import CanonCoralsMonthly, { CORALS_MONTHS, CORALS_MONTHS_ORDER } from './CanonCoralsMonthly';

/**
 * Canon Corp Campaign — CORALS · 2026 Haziran–Aralık medya planlarının TAMAMI
 * tek sayfada, tek PDF indirmesiyle.
 *
 * Efe isteği 2026-09-10: "aralığa kadar da plan yap bana hepsini tek bir linkte indirt".
 * Aylık sayfalar (`/budget/canon-corals-<ay>-2026`) olduğu gibi duruyor; bu sayfa onları
 * `embedded` modunda arka arkaya basıyor ve tek bir PDF üretiyor.
 *
 * 🔑 PDF mantığı aylık sayfayla aynı bölüm bazlı yöntem, tek farkla: her ayın
 * `[data-pdf-header]` bloğu YENİ SAYFA açar ve o andan itibaren sayfa başlığı olur.
 * Böylece PDF'te aylar birbirine karışmıyor, her ay kendi başlığıyla başlıyor.
 */
export default function CanonCoralsAll() {
  const wrapRef = useRef(null);
  const [exporting, setExporting] = useState(false);
  const [progress, setProgress] = useState('');

  const months = CORALS_MONTHS_ORDER.map((slug) => CORALS_MONTHS[slug]).filter(Boolean);
  const measured = months.filter((m) => !m.forecast).length;

  const exportPdf = useCallback(async () => {
    if (exporting || !wrapRef.current) return;
    setExporting(true);
    try {
      const root = wrapRef.current;
      // Başlıklar ve bölümler DOM sırasına göre tek listede toplanır.
      const nodes = Array.from(root.querySelectorAll('[data-pdf-header], [data-pdf-section]'));

      const shoot = (el) => html2canvas(el, {
        scale: 2,
        backgroundColor: '#ffffff',
        useCORS: true,
        width: el.offsetWidth,
        height: el.scrollHeight + 12,
        windowWidth: document.documentElement.clientWidth,
      });

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pw = pdf.internal.pageSize.getWidth();
      const ph = pdf.internal.pageSize.getHeight();
      const MG = 8;
      const GAP = 6;
      const CW = pw - MG * 2;
      const BOTTOM = ph - MG - 6;

      let headerImg = null;
      let headerH = 0;
      let pages = 0;
      let y = 0;

      const startPage = () => {
        if (pages > 0) pdf.addPage();
        pages += 1;
        if (headerImg) {
          pdf.addImage(headerImg, 'JPEG', MG, MG, CW, headerH);
          y = MG + headerH + GAP;
        } else {
          y = MG;
        }
      };

      const slice = (canvas, srcY, srcH) => {
        const c = document.createElement('canvas');
        c.width = canvas.width;
        c.height = srcH;
        const ctx = c.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, c.width, c.height);
        ctx.drawImage(canvas, 0, srcY, canvas.width, srcH, 0, 0, canvas.width, srcH);
        return c.toDataURL('image/jpeg', 0.95);
      };

      let done = 0;
      for (const el of nodes) {
        done += 1;
        setProgress(`${done} / ${nodes.length}`);

        if (el.hasAttribute('data-pdf-header')) {
          const c = await shoot(el);
          headerImg = c.toDataURL('image/jpeg', 0.95);
          headerH = (c.height * CW) / c.width;
          startPage();          // her ay yeni sayfadan başlar
          continue;
        }

        const canvas = await shoot(el);
        const pxPerMm = canvas.width / CW;
        const pageH = BOTTOM - (MG + headerH + GAP);
        let srcY = 0;
        let remain = canvas.height / pxPerMm;
        while (remain > 0.5) {
          const avail = BOTTOM - y;
          if (remain <= avail) {
            const srcH = Math.round(remain * pxPerMm);
            pdf.addImage(slice(canvas, srcY, srcH), 'JPEG', MG, y, CW, remain);
            y += remain + GAP;
            remain = 0;
            break;
          }
          if (remain <= pageH) { startPage(); continue; }
          const srcH = Math.round(avail * pxPerMm);
          pdf.addImage(slice(canvas, srcY, srcH), 'JPEG', MG, y, CW, avail);
          srcY += srcH;
          remain -= avail;
          startPage();
        }
      }

      for (let i = 1; i <= pages; i += 1) {
        pdf.setPage(i);
        pdf.setFontSize(7);
        pdf.setTextColor(150);
        pdf.text(
          `Canon CORALS — Haziran–Aralık 2026 Medya Planları · Jellybon x Glowytics · ${i}/${pages}`,
          pw / 2, ph - MG + 2, { align: 'center' },
        );
      }
      pdf.save('Canon_CORALS_2026_Haziran_Aralik_Medya_Planlari.pdf');
    } catch (e) {
      console.error('PDF export failed:', e);
    } finally {
      setProgress('');
      setExporting(false);
    }
  }, [exporting]);

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div>
          <p className="text-sm font-bold text-gray-800">Canon CORALS — Haziran–Aralık 2026 Medya Planları</p>
          <p className="text-[11px] text-gray-500 mt-0.5">
            {months.length} ay tek dosyada · her ay 3 içerik, her içerik 5 gün trafik kampanyası ·
            {' '}{measured} ay gerçekleşmeye, {months.length - measured} ay projeksiyona dayanıyor
          </p>
        </div>
        <button
          onClick={exportPdf}
          disabled={exporting}
          className="text-white text-sm font-semibold px-4 py-2 rounded-lg shadow-sm disabled:opacity-60 whitespace-nowrap"
          style={{ background: '#CC0000' }}
        >
          {exporting ? `PDF Oluşturuluyor… ${progress}` : 'Tümünü PDF İndir'}
        </button>
      </div>

      <div className="flex flex-wrap gap-1.5 mb-4">
        {months.map((m) => (
          <a
            key={m.slug}
            href={`/budget/canon-corals-${m.slug}-2026`}
            className="text-[11px] font-semibold px-3 py-1.5 rounded-lg border bg-white border-gray-200 text-gray-500 hover:bg-gray-50"
          >
            {m.label}
            {m.forecast && <span className="ml-1 text-[8px] font-bold opacity-60">tahmin</span>}
          </a>
        ))}
      </div>

      <div ref={wrapRef} className="space-y-8">
        {CORALS_MONTHS_ORDER.map((slug) => (
          <CanonCoralsMonthly key={slug} month={slug} embedded />
        ))}
      </div>
    </div>
  );
}
